<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import cltApi from 'dashboard/api/cltSimulador';

// ============================================================================
// v92: SIMULADOR CLT dentro do Chatwoot — front novo, backend = o DIGITADOR
// de producao (clt/dashboard:v86) via proxy /clt_simulador/*.
// Fluxo: ficha (CPF do contato) -> autorizar bancos -> ofertas por banco
// (todas empilhadas; melhor do cliente em lima) -> re-simular (AMARELO) ->
// digitar (VERDE) -> link de formalizacao.
// Payloads espelham o app.js v86 (spec extraida do codigo de producao).
// Tokens dos bancos: SEMPRE do Supabase (responsabilidade do digitador).
// ============================================================================

const props = defineProps({
  currentChat: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const onlyDigits = s => (s || '').replace(/\D+/g, '');
const brl = v =>
  (Number(v) || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const cents = v => brl((Number(v) || 0) / 100);
const ddmmyyyy = iso => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};

// ---------------------------------------------------------------- ficha ----
const sender = computed(() => props.currentChat?.meta?.sender || {});
const cpf = ref('');
const nome = ref('');
const nascimento = ref(''); // yyyy-mm-dd
const celular = ref('');
const enrich = ref({});
const busy = ref('');
const erroGeral = ref('');

// v93: estado do simulador PERSISTE por conversa (fecha e, ao voltar no
// mesmo cliente, reabre exatamente como estava). Cache em memória global.
const SIM_STATE =
  window.__SEGUE_SIM_STATE || (window.__SEGUE_SIM_STATE = new Map());
const stateKey = computed(() => `chat-${props.currentChat?.id}`);

onMounted(() => {
  const saved = SIM_STATE.get(stateKey.value);
  if (saved) {
    cpf.value = saved.cpf;
    nome.value = saved.nome;
    nascimento.value = saved.nascimento;
    celular.value = saved.celular;
    enrich.value = saved.enrich;
    auth.value = saved.auth;
    uy3Offers.value = saved.uy3Offers;
    factaState.value = saved.factaState;
    c6Sim.value = saved.c6Sim;
    uy3Resim.value = saved.uy3Resim || {};
    factaResim.value = saved.factaResim || {};
    c6Resim.value = saved.c6Resim || c6Resim.value;
    vinculos.value = saved.vinculos ?? null;
    vinculoSel.value = saved.vinculoSel || '';
    return;
  }
  const ca = sender.value.custom_attributes || {};
  cpf.value = onlyDigits(ca.cpf).slice(0, 11);
  // v94: NÃO puxa o nome do contato — abre só com CPF + telefone; o nome vem
  // do Enriquecer (pedido do Kairo).
  const ph = onlyDigits(sender.value.phone_number);
  celular.value = ph.startsWith('55') && ph.length >= 12 ? ph.slice(2) : ph;
});

onBeforeUnmount(() => {
  SIM_STATE.set(stateKey.value, {
    cpf: cpf.value,
    nome: nome.value,
    nascimento: nascimento.value,
    celular: celular.value,
    enrich: enrich.value,
    auth: auth.value,
    uy3Offers: uy3Offers.value,
    factaState: factaState.value,
    c6Sim: c6Sim.value,
    uy3Resim: uy3Resim.value,
    factaResim: factaResim.value,
    c6Resim: c6Resim.value,
    vinculos: vinculos.value,
    vinculoSel: vinculoSel.value,
  });
});

const cpfOk = computed(() => cpf.value.length === 11);

const enriquecer = async () => {
  if (!cpfOk.value) return;
  busy.value = 'enriquecer';
  erroGeral.value = '';
  try {
    const { data } = await cltApi.get('enriquecer', { cpf: cpf.value });
    enrich.value = data || {};
    if (data.name) nome.value = data.name;
    if (data.birthDate) nascimento.value = data.birthDate.slice(0, 10);
    if (data.chatwootPhone)
      celular.value = onlyDigits(data.chatwootPhone).replace(/^55/, '');
    useAlert('Dados enriquecidos com sucesso');
  } catch (e) {
    erroGeral.value = `Falha ao enriquecer: ${e.response?.data?.error || e.message}`;
  } finally {
    busy.value = '';
  }
};

// ------------------------------------------------------------ autorizar ----
const auth = ref(null); // { uy3: {...}, facta: {...}, c6: {...} }

const authState = banco => {
  const p = auth.value?.[banco];
  if (!p) return null;
  if (banco === 'c6') {
    const link = p.url || p.link || p.liveness_url;
    if (link)
      return {
        cls: 'roxo',
        label: p.status === 'LINK REAPROVEITADO' ? 'LINK REAPROVEITADO' : 'LINK GERADO',
        link,
      };
    return { cls: 'erro', label: p.error || p.detail || p.mensagem || 'ERRO' };
  }
  if (banco === 'uy3') {
    if (p.code === 'EXISTING_AUTH') return { cls: 'ok', label: 'JÁ AUTORIZADO' };
    if (p.error || p.detail) return { cls: 'erro', label: String(p.error || p.detail).slice(0, 80) };
    return { cls: 'aguarda', label: 'AGUARDANDO CLIENTE (termo enviado)' };
  }
  // facta
  const item = Array.isArray(p) ? p[0] : p;
  if (item?.erro === false) {
    const msg = String(item.mensagem || item.message || '').toLowerCase();
    if (/não necessita|ja autoriz|já autoriz|válido|valido/.test(msg))
      return { cls: 'ok', label: 'JÁ AUTORIZADO' };
    return { cls: 'aguarda', label: 'LINK ENVIADO (WhatsApp)' };
  }
  return {
    cls: item?.erro ? 'erro' : 'aguarda',
    label: String(item?.mensagem || item?.message || 'LINK ENVIADO').slice(0, 80),
  };
};

const autorizar = async () => {
  if (!cpfOk.value || !nome.value || !celular.value || !nascimento.value) {
    erroGeral.value =
      'Preencha CPF, nome, celular e nascimento (use Enriquecer).';
    return;
  }
  busy.value = 'autorizar';
  erroGeral.value = '';
  try {
    const { data } = await cltApi.post('multi/autorizar', {
      cpf: cpf.value,
      nome: nome.value,
      celular: onlyDigits(celular.value),
      data_nascimento: nascimento.value,
      bancos: ['uy3', 'facta', 'c6'],
    });
    auth.value = data;
  } catch (e) {
    erroGeral.value = `Falha ao autorizar: ${e.response?.data?.error || e.message}`;
  } finally {
    busy.value = '';
  }
};

const copiar = async (txt, oQue = 'Link') => {
  await copyTextToClipboard(txt);
  useAlert(`${oQue} copiado`);
};

// v93: copia a oferta formatada pro cliente (texto do Kairo), com o PRIMEIRO
// nome vindo da consulta/enriquecer.
const primeiroNome = computed(() => {
  const n = (nome.value || 'cliente').trim().split(/\s+/)[0] || 'cliente';
  return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
});

const copiarOferta = async ({ valor, prazo, parcela }) => {
  const txt =
    `Boa notícia, ${primeiroNome.value}! Consegui uma oferta pra você 💚\n\n` +
    `💰 Valor liberado: ${valor}\n` +
    `📋 Parcelas: ${prazo}x de ${parcela}\n\n` +
    `Posso seguir com a contratação?`;
  await copyTextToClipboard(txt);
  useAlert('Oferta copiada');
};

// -------------------------------------------------------------- ofertas ----
const uy3Offers = ref(null); // [{o, w}] ordenado desc
const factaState = ref(null); // {status, ofertas[], vinculos[], motivo}
const c6Sim = ref(null); // {..} | {aguardando:true} | {erro}
const buscando = ref(false);

// ---------------------------------------------------------- vínculos (v107) --
// O operador escolhe o VÍNCULO EMPREGATÍCIO antes das ofertas: cada card fica
// AZUL (ligado/elegível) ou VERMELHO (desligado/bloqueado — não selecionável).
// Fontes: FACTA (ofertas-consolidado já manda vinculos[]), UY3 consultar-margem
// (FreeMarginQuery) e, como garantia, o warranty embutido em cada oferta UY3.
// A amarração vínculo→oferta→digitação é pela MATRÍCULA, que já viaja em toda
// digitação (FACTA: of.matricula; UY3: warranty.dataprev_EmployeeCode) — por
// isso filtrar as ofertas pelo vínculo já amarra a digitação automaticamente.
const vinculos = ref(null); // null = não buscado; [] = nenhum encontrado
const vinculoSel = ref(''); // matrícula selecionada

const normStr = v => String(v ?? '').trim();

// desligado = bloqueio explícito ou elegivel explicitamente negativo
const vincLigado = v => {
  const bloqueio = normStr(
    v.tipoBloqueio_descricao || (v.tipoBloqueio && v.tipoBloqueio.descricao)
  );
  if (bloqueio && !/sem bloqueio/i.test(bloqueio)) return false;
  const eleg = v.elegivel;
  if (eleg === false) return false;
  const elegStr = normStr(eleg).toLowerCase();
  if (elegStr === 'nao' || elegStr === 'não' || elegStr === 'false') return false;
  return true;
};

const buscarMargemUY3 = async () => {
  try {
    const { data } = await cltApi.post('uy3/consultar-margem', { cpf: cpf.value });
    return data;
  } catch (e) {
    return null; // best-effort: FACTA e warranties das ofertas cobrem
  }
};

const montarVinculos = margemUy3 => {
  const mapa = new Map();
  const add = (v, fonte) => {
    const mat = normStr(v.matricula || v.dataprev_EmployeeCode);
    if (!mat) return;
    const cur = mapa.get(mat) || {
      matricula: mat,
      empregador: '',
      cnpj: '',
      admissao: '',
      salario: 0,
      margem: 0,
      ligado: true,
      motivo: '',
      fontes: [],
    };
    cur.empregador =
      cur.empregador ||
      normStr(
        v.razaoSocialEmpregador ||
          v.nomeEmpregador ||
          v.nomeRazaoSocialEmpregador ||
          v.dataprev_EmployerName
      );
    cur.cnpj = cur.cnpj || normStr(v.numeroInscricaoEmpregador || v.dataprev_EmployerRegistrationNumber);
    cur.admissao = cur.admissao || normStr(v.dataAdmissao || v.dataprev_EmployeeAdmissionDate);
    cur.salario = cur.salario || Number(v.valorTotalVencimentos ?? v.valorSalario) || 0;
    cur.margem = cur.margem || Number(v.valorMargemDisponivel ?? v.margemDisponivel) || 0;
    if (!vincLigado(v)) {
      cur.ligado = false;
      cur.motivo =
        cur.motivo ||
        normStr(
          v.tipoBloqueio_descricao ||
            (v.tipoBloqueio && v.tipoBloqueio.descricao) ||
            v.motivoInelegibilidade_descricao ||
            (v.motivoInelegibilidade && v.motivoInelegibilidade.descricao) ||
            v.motivoInelegibilidade
        );
    }
    if (!cur.fontes.includes(fonte)) cur.fontes.push(fonte);
    mapa.set(mat, cur);
  };
  // UY3 consultar-margem (cru: array de datasets com result[])
  const listaUy3 = Array.isArray(margemUy3)
    ? margemUy3.flatMap(d => (Array.isArray(d?.result) ? d.result : []))
    : [];
  listaUy3.forEach(v => add(v, 'UY3'));
  // FACTA (backend já filtra pelos elegíveis)
  (factaState.value?.vinculos || []).forEach(v => add(v, 'FACTA'));
  // garantia: empregador do warranty de cada oferta UY3 (toda oferta tem vínculo)
  (uy3Offers.value || []).forEach(c =>
    add({ ...c.w, matricula: c.w?.dataprev_EmployeeCode }, 'UY3')
  );
  // ligados primeiro
  return [...mapa.values()].sort((a, b) => (b.ligado ? 1 : 0) - (a.ligado ? 1 : 0));
};

const selecionarVinculo = v => {
  if (!v.ligado) return;
  vinculoSel.value = v.matricula;
};

// ofertas só aparecem sem seleção quando NÃO existe vínculo algum (fallback:
// nunca travar a tela se as fontes não devolverem vínculo)
const mostrarOfertas = computed(
  () => !(vinculos.value || []).length || !!vinculoSel.value
);
// oferta sem matrícula não tem como amarrar — continua visível
const uy3CardVisivel = card => {
  const mat = normStr(card.w?.dataprev_EmployeeCode);
  return !vinculoSel.value || !mat || mat === vinculoSel.value;
};
const factaCardVisivel = of => {
  const mat = normStr(of.matricula);
  return !vinculoSel.value || !mat || mat === vinculoSel.value;
};

const uy3ParcelaReal = o => {
  const items = o.paymentScheduleItems;
  if (Array.isArray(items)) {
    const p = items.find(i => Number(i.payment) > 0);
    if (p) return Number(p.payment);
  }
  return null;
};

// v100: quebra o productName da UY3 pro card novo — "LEILÃO - CLT - 3C -
// Seguro Sabemi - V2" vira Produto "CLT - 3C" (LEILÃO já é badge e o lugar
// do seguro agora mostra o ID da simulação). Sem seguro/leilão, mostra tudo.
const uy3Produto = pn => {
  let s = String(pn || '').replace(/leil[aã]o\s*-?\s*/i, '');
  const ix = s.search(/seguro/i);
  if (ix >= 0) s = s.slice(0, ix);
  return s.replace(/^[\s·-]+|[\s·-]+$/g, '').trim() || '—';
};

const buscarUY3 = async () => {
  try {
    const { data } = await cltApi.post('uy3/ofertas', {
      cpf: cpf.value,
      rangeNumberOfPayments: [12, 18, 24, 36, 48, 60, 72, 84],
    });
    const list = Array.isArray(data) ? data : data?.offers || data?.result || [];
    uy3Offers.value = list
      .filter(x => x && x.amortization)
      .map(x => ({ o: x.amortization, w: x.warranty || {} }))
      .sort((a, b) => (b.o.liquidValue || 0) - (a.o.liquidValue || 0));
    uy3Resim.value = {};
    uy3Offers.value.forEach((_, i) => {
      uy3Resim.value[i] = { modo: 'Liquid', valor: '', busy: false, erro: '' };
    });
  } catch (e) {
    uy3Offers.value = [];
    return e.response?.data?.detail || e.message;
  }
  return null;
};

const buscarFACTA = async () => {
  try {
    cltApi
      .post('facta/log-busca-ofertas', {
        cpf: cpf.value,
        data_nascimento: ddmmyyyy(nascimento.value),
      })
      .catch(() => {});
    const { data } = await cltApi.post('facta/ofertas-consolidado', {
      cpf: cpf.value,
      data_nascimento: ddmmyyyy(nascimento.value),
      telefone: onlyDigits(celular.value),
    });
    const ofertas = (data.ofertas || [])
      .slice()
      .sort((a, b) => (b.valor_liquido || 0) - (a.valor_liquido || 0));
    factaState.value = {
      status: data.status,
      ofertas,
      vinculos: data.vinculos || [],
      motivo: data.motivo_negado || '',
    };
    factaResim.value = {};
    ofertas.forEach((of, i) => {
      factaResim.value[i] = { parcela: of.parcela, busy: false, erro: '' };
    });
  } catch (e) {
    factaState.value = {
      status: 'erro',
      ofertas: [],
      vinculos: [],
      motivo: e.response?.data?.detail || e.message,
    };
  }
};

const C6_WAIT_RE = /WAITING|PENDING|NOT_AUTH|invalid request body|not-auth|liveness|consent/i;

const buscarC6 = async () => {
  try {
    const { data } = await cltApi.post('c6/simular', {
      cpf: cpf.value,
      tipo_simulacao: '100O',
    });
    if (data.id_simulacao) c6Sim.value = data;
    else if (C6_WAIT_RE.test(JSON.stringify(data)))
      c6Sim.value = { aguardando: true };
    else c6Sim.value = { erro: data.detail || data.error || 'sem oferta' };
  } catch (e) {
    const raw = JSON.stringify(e.response?.data || e.message);
    c6Sim.value = C6_WAIT_RE.test(raw)
      ? { aguardando: true }
      : { erro: (e.response?.data?.detail || e.message || '').slice(0, 120) };
  }
};

const buscarOfertas = async () => {
  if (!cpfOk.value || !nascimento.value) {
    erroGeral.value = 'CPF e nascimento são obrigatórios (use Enriquecer).';
    return;
  }
  buscando.value = true;
  erroGeral.value = '';
  const [uy3Err, , , margemUy3] = await Promise.all([
    buscarUY3(),
    buscarFACTA(),
    buscarC6(),
    buscarMargemUY3(),
  ]);
  if (uy3Err) erroGeral.value = `UY3: ${uy3Err}`;
  // v107: monta os vínculos e resolve a seleção — mantém a escolha anterior
  // se ela ainda existir; com UM único vínculo ligado, seleciona sozinho.
  vinculos.value = montarVinculos(margemUy3);
  const ligados = vinculos.value.filter(v => v.ligado);
  if (!ligados.some(v => v.matricula === vinculoSel.value)) {
    vinculoSel.value = ligados.length === 1 ? ligados[0].matricula : '';
  }
  buscando.value = false;
};

// melhor do cliente (maior valor liquido entre TODOS os bancos, em reais)
const melhorValor = computed(() => {
  const vals = [];
  (uy3Offers.value || []).forEach(x => vals.push((x.o.liquidValue || 0) / 100));
  (factaState.value?.ofertas || []).forEach(o => vals.push(Number(o.valor_liquido) || 0));
  if (c6Sim.value?.id_simulacao) vals.push(Number(c6Sim.value.valor_solicitado) || 0);
  return vals.length ? Math.max(...vals) : 0;
});
const isMelhor = valorReais =>
  melhorValor.value > 0 && Math.abs(valorReais - melhorValor.value) < 0.005;

// ----------------------------------------------------------- re-simular ----
const uy3Resim = ref({}); // idx -> {modo, valor, busy}
const c6Resim = ref({ tipo: 'POR_VALOR_PARCELA', valor: '', prazo: '', busy: false });
const factaResim = ref({}); // idx -> {parcela, busy}

const resimUY3 = async idx => {
  const st = uy3Resim.value[idx];
  const card = uy3Offers.value[idx];
  st.erro = '';
  const valor = Number(String(st.valor).replace(/\./g, '').replace(',', '.'));
  if (!valor || valor <= 0) {
    st.erro = 'Informe o valor para simular (ex: 2000,00).';
    return;
  }
  st.busy = true;
  try {
    const o = card.o;
    const { data } = await cltApi.post('uy3/amortizar', {
      legalPerson: false,
      productId: o.productId,
      amortization: {
        agentCommission: { amount: 0, type: 'Percentage', baseValue: 'InitialValue' },
        apr: o.apr,
        startDate: o.startDate,
        requestedAmount: Math.round(valor * 100),
        amortizationType: o.amortizationType || 'price',
        calculateByValueType: st.modo,
        firstPaymentDate: o.firstPaymentDate,
        numberOfPayments: o.numberOfPayments,
        numberOfInterestPayments: o.numberOfInterestPayments || 0,
        calculationType: o.calculationType || 'V360Meses',
        paymentPeriodicity: o.paymentPeriodicity || { every: 1, periodicity: 'Monthly' },
        includePaymentFixedCosts: true,
        financeTaxExempted: false,
        dueDateOnBusinessDays: false,
      },
    });
    const r = data.amortization || data;
    if (r.liquidValue == null || r.code || r.error) {
      st.erro = `UY3: ${r.message || r.error || 'simulação inválida'}`;
      return;
    }
    const parcela = uy3ParcelaReal(r);
    card.o = { ...o, ...r, calculateByValueType: st.modo };
    card.parcelaReal = parcela;
    if (parcela) card.w = { ...card.w, totalValue: parcela / 100 };
    useAlert('UY3 re-simulado');
  } catch (e) {
    const d = e.response?.data || {};
    st.erro = `UY3: ${d.detail || d.message || d.error || e.message}`;
  } finally {
    st.busy = false;
  }
};

const resimC6 = async () => {
  const st = c6Resim.value;
  const valor = Number(String(st.valor).replace(',', '.'));
  const prazo = parseInt(st.prazo, 10);
  if (!valor || !prazo) return;
  st.busy = true;
  try {
    const body = { cpf: cpf.value, tipo_simulacao: st.tipo, prazo };
    if (st.tipo === 'POR_VALOR_SOLICITADO') body.valor_solicitado = valor;
    else body.valor_parcela = valor;
    const { data } = await cltApi.post('c6/simular', body);
    if (data.id_simulacao) {
      c6Sim.value = data;
      useAlert('C6 re-simulado');
    } else useAlert(`C6: ${data.detail || data.error || 'simulação inválida'}`);
  } catch (e) {
    useAlert(`C6: ${e.response?.data?.detail || e.message}`);
  } finally {
    st.busy = false;
  }
};

// FACTA: etapa1 com nova parcela (valor = parcela / coeficiente)
const resimFACTA = async idx => {
  const st = factaResim.value[idx];
  const of = factaState.value.ofertas[idx];
  st.erro = '';
  const parcela = Number(String(st.parcela).replace(/\./g, '').replace(',', '.'));
  const coef = Number(of.coeficiente);
  if (!parcela) {
    st.erro = 'Informe o valor da parcela para simular.';
    return;
  }
  if (!coef) {
    st.erro = 'Oferta sem coeficiente — não dá pra re-simular esta tabela.';
    return;
  }
  st.busy = true;
  try {
    const valorOp = parcela / coef;
    const { data } = await cltApi.post('facta/etapa1-simulador', {
      produto: 'D',
      tipo_operacao: '13',
      averbador: '10010',
      convenio: '3',
      cpf: cpf.value,
      data_nascimento: ddmmyyyy(nascimento.value),
      prazo: String(of.prazo),
      codigo_tabela: String(of.codigoTabela),
      valor_operacao: valorOp.toFixed(2),
      valor_parcela: (Math.round(valorOp * coef * 100) / 100).toFixed(2),
      coeficiente: String(of.coeficiente),
      matricula: of.matricula || undefined,
    });
    const idSim = data.id_simulador || data.id || data.idSimulador;
    if (!idSim) {
      const extra = data.prestacao_maxima
        ? ` (parcela máx: ${data.prestacao_maxima})`
        : '';
      st.erro = `FACTA: ${data.mensagem || 'simulação recusada'}${extra}`;
      return;
    }
    of._idSimulador = idSim;
    of.parcela = parcela;
    of.contrato = valorOp;
    of.valor_liquido = valorOp; // aproximação até operacoes-disponiveis
    try {
      const vinc = (factaState.value.vinculos || []).find(
        v => String(v.matricula) === String(of.matricula)
      );
      const { data: ops } = await cltApi.get('facta/operacoes-disponiveis', {
        cpf: cpf.value,
        data_nascimento: ddmmyyyy(nascimento.value),
        valor_parcela: parcela.toFixed(2),
        valor_renda: Number(vinc?.valorTotalVencimentos || 0).toFixed(2),
        prazo: String(of.prazo),
        opcao_valor: '2',
        ...(of.matricula ? { matricula: of.matricula } : {}),
      });
      const lista = ops.tabelas || ops.opcoes || ops.operacoes || [];
      const real = lista.find(t => String(t.codigoTabela) === String(of.codigoTabela));
      if (real) {
        of.contrato = Number(real.contrato) || of.contrato;
        of.valor_liquido = Number(real.valor_liquido) || of.valor_liquido;
      }
    } catch (e) {
      // valores reais são best-effort; a simulação (id) já está garantida
    }
    useAlert('FACTA re-simulado');
  } catch (e) {
    const d = e.response?.data || {};
    st.erro = `FACTA: ${d.mensagem || d.detail || e.message}`;
  } finally {
    st.busy = false;
  }
};

// --------------------------------------------------------------- digitar ---
const view = ref('ofertas'); // ofertas | digitar | resultado
const dig = ref(null); // { banco, resumo:{valor,prazo,parcela,extra}, ctx }
const form = ref({});
const digBusy = ref(false);
const resultado = ref(null);

const _fakeEmail = n => {
  const p = (n || 'cliente').toLowerCase().normalize('NFD').replace(/[^a-z ]/g, '').split(' ').filter(Boolean);
  const base = (p[0] || 'cliente') + (p.length > 1 ? p[p.length - 1] : '');
  const nn = Math.floor(10 + Math.random() * 89);
  return `${base}${nn}@${Math.random() < 0.5 ? 'gmail' : 'hotmail'}.com`;
};
const _fakeHouseNumber = v =>
  v && v !== '0' ? v : String(100 + Math.floor(Math.random() * 400));

const baseForm = () => ({
  nome: nome.value,
  nascimento: nascimento.value,
  email: enrich.value.email || _fakeEmail(nome.value),
  telefone: onlyDigits(celular.value),
  cep: onlyDigits(enrich.value.postalCode || ''),
  rua: enrich.value.street || '',
  numero: _fakeHouseNumber(enrich.value.addressNumber || ''),
  complemento: '',
  bairro: enrich.value.neighborhood || '',
  cidade: enrich.value.city || '',
  uf: (enrich.value.state || '').toUpperCase(),
});

const abrirDigitarUY3 = async (idx) => {
  digBusy.value = true;
  try {
    const { data } = await cltApi.get('uy3/propostas-ativas', { cpf: cpf.value });
    if ((data.propostas || []).length) {
      useAlert('Este CPF já tem proposta UY3 em andamento — resolva no digitador antes.');
      return;
    }
  } catch (e) {
    // segue: o backend da proposta valida de novo
  } finally {
    digBusy.value = false;
  }
  const card = uy3Offers.value[idx];
  dig.value = {
    banco: 'UY3',
    ctx: card,
    resumo: {
      valor: cents(card.o.liquidValue),
      prazo: `${card.o.numberOfPayments}x`,
      parcela: card.parcelaReal ? cents(card.parcelaReal) : '—',
      extra: card.o.productName || '',
    },
  };
  form.value = {
    ...baseForm(),
    civilStatus: 'Single',
    occupation: 'Trabalhador',
    pixTipo: 'NaturalRegistrationNumber',
    pixChave: cpf.value,
  };
  view.value = 'digitar';
};

const abrirDigitarFACTA = async idx => {
  const of = factaState.value.ofertas[idx];
  if (!of._idSimulador) {
    // etapa1 com os valores atuais do card (gera o id_simulador)
    factaResim.value[idx] = factaResim.value[idx] || { parcela: of.parcela, busy: false };
    factaResim.value[idx].parcela = of.parcela;
    await resimFACTA(idx);
    if (!of._idSimulador) return;
  }
  dig.value = {
    banco: 'FACTA',
    ctx: of,
    resumo: {
      valor: brl(of.valor_liquido),
      prazo: `${of.prazo}x`,
      parcela: brl(of.parcela),
      extra: of.tabela || '',
    },
  };
  form.value = {
    ...baseForm(),
    sexo: 'M',
    estadoCivil: 'solteiro',
    nacionalidade: 'brasileira',
    nomeMae: enrich.value.motherName || '',
    rg: enrich.value.rgNumber || cpf.value,
    rgOrgao: enrich.value.rgIssuerOrg || 'SSP',
    rgUf: (enrich.value.rgIssuerUf || enrich.value.state || '').toUpperCase(),
    rgData: ddmmyyyy(enrich.value.rgIssueDate || '2023-04-05'),
    renda: '',
    matricula: of.matricula || '',
    pixTipo: '1',
    pixChave: cpf.value,
  };
  view.value = 'digitar';
};

const abrirDigitarC6 = () => {
  const s = c6Sim.value;
  dig.value = {
    banco: 'C6',
    ctx: s,
    resumo: {
      valor: brl(s.valor_solicitado),
      prazo: `${s.prazo}x`,
      parcela: brl(s.valor_parcela),
      extra: `IOF ${brl(s.valor_iof)}`,
    },
  };
  form.value = {
    ...baseForm(),
    tipoConta: 'ContaCorrenteIndividual',
    numeroBanco: '',
    numeroAgencia: '',
    digitoAgencia: '',
    numeroConta: '',
    digitoConta: '',
  };
  view.value = 'digitar';
};

// ViaCEP direto do browser (igual o digitador faz)
const buscarCep = async () => {
  const c = onlyDigits(form.value.cep);
  if (c.length !== 8) return;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${c}/json/`);
    const d = await r.json();
    if (d.erro) return;
    form.value.rua = d.logradouro || form.value.rua;
    form.value.bairro = d.bairro || form.value.bairro;
    form.value.cidade = d.localidade || form.value.cidade;
    form.value.uf = (d.uf || form.value.uf).toUpperCase();
    useAlert('Endereço preenchido pelo CEP');
  } catch (e) {
    // silencioso — usuário preenche na mão
  }
};

const etiquetarLink = () => {
  cltApi
    .post('chatwoot/etiquetar-link', { telefone: onlyDigits(celular.value) })
    .catch(() => {});
};

const ESTADO_CIVIL_FACTA = { solteiro: '1', casado: '2', divorciado: '3', viuvo: '4', separado: '5', uniao: '6' };
const NACIONALIDADE_FACTA = { brasileira: '1', naturalizada: '2', estrangeira: '3' };

const confirmarDigitacao = async () => {
  digBusy.value = true;
  erroGeral.value = '';
  try {
    if (dig.value.banco === 'UY3') await digitarUY3();
    else if (dig.value.banco === 'FACTA') await digitarFACTA();
    else await digitarC6();
  } catch (e) {
    const d = e.response?.data || {};
    erroGeral.value = `${dig.value.banco}: ${d.mensagem || d.detail || d.error || e.message}`;
  } finally {
    digBusy.value = false;
  }
};

const digitarUY3 = async () => {
  const f = form.value;
  const { o, w } = dig.value.ctx;
  // 1. pessoa
  const { data: p } = await cltApi.post('uy3/pessoa', {
    registrationNumber: cpf.value,
    name: f.nome,
    email: f.email,
    phone: onlyDigits(f.telefone),
    birthDate: `${f.nascimento}T00:00:00Z`,
    civilStatus: f.civilStatus,
    nationality: 'Brasileira',
    occupation: f.occupation || 'Trabalhador',
    address: {
      zipCode: onlyDigits(f.cep),
      addressName: f.rua,
      number: f.numero,
      complement: f.complemento || '',
      district: f.bairro,
      city: f.cidade,
      uf: (f.uf || '').toUpperCase(),
    },
  });
  // UY3 devolve o id como string COM aspas — mesmo tratamento do digitador antigo
  const personId =
    typeof p === 'string' ? p.replace(/"/g, '') : p.id || p.personId;
  if (!personId) throw new Error('UY3 não devolveu personId');
  // 2. conta (PIX)
  let keyPix = f.pixChave;
  if (f.pixTipo === 'NaturalRegistrationNumber') keyPix = cpf.value;
  if (f.pixTipo === 'Phone') keyPix = onlyDigits(keyPix);
  const { data: c } = await cltApi.post('uy3/conta', {
    personId,
    contas: [
      { operationTypeValue: 'Pix', pixKeyTypeValue: f.pixTipo, keyPix },
    ],
  });
  // UY3 devolve a conta como LISTA (uuid cru ou objeto) — igual o digitador antigo
  const contaItem = Array.isArray(c) ? c[0] : c;
  const bankAccountId =
    typeof contaItem === 'string'
      ? contaItem.replace(/"/g, '')
      : contaItem?.id || contaItem?.bankAccountId;
  if (!bankAccountId) throw new Error('UY3 não devolveu bankAccountId');
  // 3. proposta
  const { data: r } = await cltApi.post(
    'uy3/proposta',
    {
      productId: o.productId,
      personId,
      beneficiaryId: personId,
      bankAccountId,
      liquidationType: 'EletronicTransfer',
      amortization: {
        agentCommission: { amount: 0, type: 'Percentage', baseValue: 'InitialValue' },
        firstPaymentDate: o.firstPaymentDate,
        numberOfInterestPayments: o.numberOfInterestPayments || 0,
        apr: o.apr,
        startDate: o.startDate,
        requestedAmount: o.requestedAmount,
        amortizationType: o.amortizationType || 'price',
        calculateByValueType: o.calculateByValueType || 'Payment',
        numberOfPayments: o.numberOfPayments,
        calculationType: o.calculationType || 'V360Meses',
        paymentPeriodicity: o.paymentPeriodicity || { every: 1, periodicity: 'Monthly' },
      },
      warranty: [
        {
          warrantyType: 'DataprevEmployee',
          productId: o.productId,
          dataprev_EmployerName: w.dataprev_EmployerName,
          dataprev_EmployerRegistrationNumber: w.dataprev_EmployerRegistrationNumber,
          dataprev_EmployerRegistrationCode: w.dataprev_EmployerRegistrationCode,
          dataprev_EmployeeCode: w.dataprev_EmployeeCode,
          dataprev_DiscountStartPeriod: w.dataprev_DiscountStartPeriod,
          totalValue: Math.round((w.totalValue || 0) * 100),
          dataprev_EmployeeAdmissionDate: w.dataprev_EmployeeAdmissionDate,
          datasetId: w.datasetId,
        },
      ],
    },
    { 'X-Cpf': cpf.value, 'X-Telefone': onlyDigits(f.telefone) }
  );
  const signUrl = extraiUrl(r.signUrl);
  if (signUrl) {
    etiquetarLink();
    resultado.value = {
      banco: 'UY3',
      ok: true,
      numero: r.creditNoteId,
      link: signUrl,
      msg: 'Proposta digitada — link de assinatura pronto.',
    };
  } else {
    // link ainda não saiu: registra pendência (o digitador envia sozinho)
    cltApi
      .post('uy3/link-pendente', {
        creditNoteId: r.creditNoteId,
        cpf: cpf.value,
        telefone: onlyDigits(f.telefone),
        valorLiquido: o.requestedAmount,
        valorParcela: dig.value.ctx.parcelaReal || null,
        prazo: o.numberOfPayments,
        usuario: null,
      })
      .catch(() => {});
    resultado.value = {
      banco: 'UY3',
      ok: true,
      numero: r.creditNoteId,
      link: null,
      msg: 'Proposta digitada! O link de assinatura será enviado automaticamente na conversa assim que o banco liberar.',
    };
  }
  view.value = 'resultado';
};

const digitarFACTA = async () => {
  const f = form.value;
  const of = dig.value.ctx;
  // cidade -> código FACTA
  const cidadeCod = await factaCidadeCodigo(f.uf, f.cidade);
  const { data: e2 } = await cltApi.post('facta/etapa2-dados-pessoais', {
    id_simulador: Number(of._idSimulador),
    cpf: cpf.value,
    nome: f.nome,
    sexo: f.sexo,
    estado_civil: ESTADO_CIVIL_FACTA[f.estadoCivil] || '1',
    data_nascimento: ddmmyyyy(f.nascimento),
    rg: f.rg,
    estado_rg: f.rgUf,
    orgao_emissor: f.rgOrgao,
    data_expedicao: f.rgData,
    estado_natural: f.uf,
    cidade_natural: cidadeCod,
    nacionalidade: NACIONALIDADE_FACTA[f.nacionalidade] || '1',
    celular: fmtCelularFacta(f.telefone),
    email: f.email,
    renda: Number(String(f.renda || '0').replace(',', '.')).toFixed(2),
    cep: onlyDigits(f.cep),
    endereco: f.rua,
    numero: f.numero,
    bairro: f.bairro,
    cidade: cidadeCod,
    estado: f.uf,
    nome_mae: f.nomeMae,
    nome_pai: 'NAO DECLARADO',
    matricula: String(f.matricula || ''),
    tipo_chave_pix: f.pixTipo,
    chave_pix: f.pixChave,
    cliente_iletrado_impossibilitado: 'N',
  });
  const codigoCliente = e2.codigo_cliente || e2.codigoCliente || e2.codigo;
  if (!codigoCliente)
    throw new Error(e2.mensagem || e2.erro || 'FACTA etapa2 sem codigo_cliente');
  const { data: e3 } = await cltApi.post('facta/etapa3-proposta', {
    codigo_cliente: Number(codigoCliente),
    id_simulador: Number(of._idSimulador),
    tipo_formalizacao: 'DIG',
  });
  const codigoAf = e3.codigo || e3.codigo_af || e3.id;
  const link = e3.url_formalizacao || e3.urlFormalizacao || e3.link;
  if (link) etiquetarLink();
  resultado.value = {
    banco: 'FACTA',
    ok: true,
    numero: codigoAf,
    link: link || null,
    msg: link
      ? 'Proposta digitada — link de formalização pronto.'
      : 'Proposta digitada (AF gerado).',
  };
  view.value = 'resultado';
};

const digitarC6 = async () => {
  const f = form.value;
  const tel = onlyDigits(f.telefone);
  const tel11 = tel.length === 10 ? `${tel.slice(0, 2)}9${tel.slice(2)}` : tel;
  const { data: r } = await cltApi.post('c6/incluir-proposta', {
    id_simulacao: String(dig.value.ctx.id_simulacao),
    cpf: cpf.value,
    ddd: tel11.slice(0, 2),
    numero_telefone: tel11.slice(2),
    logradouro: f.rua,
    numero: f.numero,
    cep: onlyDigits(f.cep),
    bairro: f.bairro,
    cidade: f.cidade,
    uf: (f.uf || '').toUpperCase(),
    dados_bancarios: {
      tipo_conta: f.tipoConta,
      numero_banco: onlyDigits(f.numeroBanco),
      numero_agencia: onlyDigits(f.numeroAgencia),
      digito_agencia: f.digitoAgencia || '',
      numero_conta: onlyDigits(f.numeroConta),
      digito_conta: f.digitoConta || '',
    },
  });
  const numero = r.numero_proposta || r.numeroProposta || r.proposta;
  if (!numero) throw new Error(r.detail || r.mensagem || 'C6 sem numero_proposta');
  let link = null;
  try {
    const { data: fm } = await cltApi.get(`c6/formalizacao/${numero}`);
    link = extraiUrl(fm.url || fm.link || fm);
  } catch (e) {
    // formalização pode demorar — não é erro
  }
  if (link) etiquetarLink();
  resultado.value = {
    banco: 'C6',
    ok: true,
    numero,
    link,
    msg: link
      ? 'Proposta digitada — link de formalização pronto.'
      : 'Proposta digitada! O link de formalização fica disponível em instantes.',
  };
  view.value = 'resultado';
};

const factaCidadeCodigo = async (uf, cidade) => {
  const norm = s =>
    String(s || '')
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const { data } = await cltApi.get('facta/cidade', {
    estado: (uf || '').toUpperCase(),
    nome_cidade: norm(cidade),
  });
  const mapa = data.cidade || data.cidades || {};
  const alvo = norm(cidade);
  const hit = Object.entries(mapa).find(([, v]) => norm(v?.nome || v) === alvo);
  if (!hit) throw new Error(`Cidade "${cidade}/${uf}" não encontrada na FACTA`);
  return hit[0];
};

const fmtCelularFacta = tel => {
  const d = onlyDigits(tel);
  const n = d.length === 10 ? `${d.slice(0, 2)}9${d.slice(2)}` : d;
  return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`;
};

const extraiUrl = v => {
  if (!v) return null;
  if (typeof v === 'string') {
    const s = v.trim().replace(/^"|"$/g, '');
    return s.startsWith('http') ? s : null;
  }
  if (Array.isArray(v)) {
    for (const it of v) {
      const u = extraiUrl(it?.url || it);
      if (u) return u;
    }
    return null;
  }
  return extraiUrl(v.url || v.signUrl || v.link);
};

const voltarOfertas = () => {
  clearInterval(propostasTimer);
  propostasTimer = null;
  view.value = 'ofertas';
  dig.value = null;
  erroGeral.value = '';
};

// ----------------------------------------------- consultar propostas (v94) --
// Porta a tela "Status de Proposta" do digitador antigo pra dentro do modal:
// propostas-cliente (3 bancos em paralelo) + AO VIVO (refresh 60s) + ações
// Copiar link / Buscar link / Reapresentar PIX / Cancelar (backend só id 1).
const propostas = ref(null); // { uy3, facta, c6 }
const propostasBusy = ref(false);
const reapr = ref({}); // creditNoteId -> { open, tipo, chave, busy }
let propostasTimer = null;

const carregarPropostas = async () => {
  if (!cpfOk.value) return;
  propostasBusy.value = true;
  try {
    const { data } = await cltApi.get('propostas-cliente', { cpf: cpf.value });
    propostas.value = data;
  } catch (e) {
    erroGeral.value = `Propostas: ${e.response?.data?.detail || e.message}`;
  } finally {
    propostasBusy.value = false;
  }
};

const abrirPropostas = () => {
  if (!cpfOk.value) {
    erroGeral.value = 'Informe o CPF para consultar as propostas.';
    return;
  }
  erroGeral.value = '';
  propostas.value = null;
  view.value = 'propostas';
  carregarPropostas();
  clearInterval(propostasTimer);
  propostasTimer = setInterval(carregarPropostas, 60000);
};

const fecharPropostas = () => {
  clearInterval(propostasTimer);
  propostasTimer = null;
  voltarOfertas();
};

onBeforeUnmount(() => clearInterval(propostasTimer));

const statusCls = st => {
  const s = String(st || '').toUpperCase();
  if (/PAID|DISBURSED|FUNDED|LIQUIDAD|CONCLUID|FINISHED|COMPLET/.test(s)) return 'ok';
  if (/FORMAL|SIGNAT|COLETA|SIGNING/.test(s)) return 'info';
  if (/REJECT|CANCEL|FAIL|EXPIR|ERROR/.test(s)) return 'err';
  return 'pending';
};
const STATUS_BADGE = {
  ok: 'bg-n-teal-9 text-white',
  info: 'bg-n-blue-9 text-white',
  err: 'bg-n-ruby-9 text-white',
  pending: 'bg-n-amber-9 text-black-900',
};
const fmtData = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const UY3_CANCEL_RE = /Cancel|Reject|Expir|Fail/i;
const uy3Items = computed(() => {
  const p = propostas.value?.uy3;
  const items = Array.isArray(p) ? p : [];
  // canceladas por último; com link primeiro (mesma ordem do digitador antigo)
  return [...items].sort((a, b) => {
    const ca = UY3_CANCEL_RE.test(String(a.status || '')) ? 1 : 0;
    const cb = UY3_CANCEL_RE.test(String(b.status || '')) ? 1 : 0;
    if (ca !== cb) return ca - cb;
    return (extraiUrl(a.__signUrl) ? 0 : 1) - (extraiUrl(b.__signUrl) ? 0 : 1);
  });
});
const factaItems = computed(() => {
  const p = propostas.value?.facta;
  if (Array.isArray(p)) return p;
  if (p && Array.isArray(p.propostas)) return p.propostas;
  return [];
});
const c6Items = computed(() =>
  Array.isArray(propostas.value?.c6) ? propostas.value.c6 : []
);
const bancoErro = key => {
  const p = propostas.value?.[key];
  return p && !Array.isArray(p) ? p.error || '' : '';
};

const isRevisaoPix = st => {
  const s = String(st || '').toLowerCase();
  return (s.includes('revis') && s.includes('pagamento')) || s.includes('paymentrevision');
};
const uy3Finalizada = p =>
  /PAID|PAGO|LIQUIDAD|CONCLU|FINISH|ENCERRAD|COMPLET|CANCEL|REJECT|REPROV|DISAPPROV|EXPIR|FAIL/i.test(
    `${p.__resumo?.status || p.status || ''} ${p.__resumo?.statusDisplay || p.statusDisplay || ''}`
  );

const buscarLinkUY3 = async p => {
  try {
    const { data } = await cltApi.get(`uy3/signurl/${p.id}`);
    if (extraiUrl(data)) {
      p.__signUrl = data;
      useAlert('Link de assinatura disponível');
    } else {
      useAlert('UY3 ainda não gerou o link (biometria pendente).');
    }
  } catch (e) {
    useAlert(`UY3: ${e.response?.data?.detail || e.message}`);
  }
};

const abrirReapr = p => {
  reapr.value[p.id] = { open: true, tipo: 'NaturalRegistrationNumber', chave: cpf.value, busy: false };
};
const ajustaChaveReapr = id => {
  const st = reapr.value[id];
  st.chave = st.tipo === 'NaturalRegistrationNumber' ? cpf.value : '';
};
const submitReapr = async id => {
  const st = reapr.value[id];
  let chave = String(st.chave || '').trim();
  if (st.tipo === 'NaturalRegistrationNumber') chave = cpf.value;
  if (st.tipo === 'Phone') chave = onlyDigits(chave);
  if (!chave) {
    useAlert('Informe a chave PIX');
    return;
  }
  st.busy = true;
  try {
    await cltApi.post('uy3/reapresentar-pagamento', {
      creditNoteId: id,
      pixKeyTypeValue: st.tipo,
      keyPix: chave,
    });
    useAlert('Pagamento reapresentado com sucesso');
    st.open = false;
    carregarPropostas();
  } catch (e) {
    useAlert(`Erro ao reapresentar: ${e.response?.data?.detail || e.message}`);
  } finally {
    st.busy = false;
  }
};

const cancelarUY3 = async p => {
  const label = p.creditNoteNo || p.id;
  // eslint-disable-next-line no-alert
  if (!window.confirm(`Tem certeza que deseja CANCELAR a proposta ${label}?\n\nEsta ação é IRREVERSÍVEL.`)) return;
  try {
    await cltApi.post('uy3/cancelar-proposta', { creditNoteId: p.id });
    useAlert('Proposta cancelada com sucesso');
    carregarPropostas();
  } catch (e) {
    useAlert(
      e.response?.status === 403
        ? 'Apenas o administrador (id 1) pode cancelar propostas.'
        : `Erro ao cancelar: ${e.response?.data?.detail || e.message}`
    );
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div
    class="flex fixed inset-0 z-50 justify-center items-start p-4 bg-black-900/70"
    @click.self="emit('close')"
  >
    <!-- v94: borda verde + fundo verde-escuro pra destacar do sistema atrás -->
    <div
      class="flex flex-col w-full max-h-[94vh] mt-2 rounded-xl border-2 shadow-2xl border-green-400 bg-green-900"
      :class="view === 'digitar' ? 'max-w-7xl' : 'max-w-6xl'"
    >
      <!-- header (v100: padrão novo do Kairo — ícone verde no quadrado,
           título forte, nome do cliente em caixa alta) -->
      <div class="flex gap-3 items-center px-4 py-3 border-b border-n-weak">
        <span class="sim-ico"><span class="i-lucide-calculator size-4" /></span>
        <h3 class="text-base font-bold text-white">
          Simulador CLT
          <span class="text-sm font-normal uppercase text-n-slate-11">· {{ nome || 'cliente' }}</span>
        </h3>
        <span class="flex-1" />
        <button
          v-if="view !== 'ofertas'"
          type="button"
          class="px-3 py-1 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
          @click="voltarOfertas"
        >
          ← Voltar às ofertas
        </button>
        <button
          type="button"
          class="px-4 py-1.5 text-xs font-bold text-white bg-red-500 rounded-xl hover:bg-red-600"
          @click="emit('close')"
        >
          ✕ Fechar
        </button>
      </div>

      <div class="overflow-auto flex-1 p-4">
        <p v-if="erroGeral" class="p-2 mb-3 text-sm rounded-lg border border-n-ruby-7 bg-n-ruby-2 text-n-ruby-11">
          {{ erroGeral }}
        </p>

        <!-- ================= VIEW: OFERTAS ================= -->
        <template v-if="view === 'ofertas'">
          <!-- ficha (v100: padrão novo do Kairo — cartão escuro, ícones verdes,
               valores em negrito; os campos continuam EDITÁVEIS, só mudou o
               visual. Botões ganham ícone, mesmas cores/ações de antes.) -->
          <div class="p-3 mb-3 rounded-xl border border-n-weak bg-n-solid-1">
            <div class="ficha-grid">
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-circle-user size-4" /> CPF</span>
                <!-- v106: aceita CPF com ponto/traço e limpa sozinho (só
                     dígitos, máx 11). SEM maxlength: ele truncava o CPF
                     mascarado colado ANTES da limpeza. -->
                <input
                  :value="cpf"
                  placeholder="—"
                  class="ficha-input tabular-nums"
                  :class="{ 'ficha-input-erro': cpf && !cpfOk }"
                  @input="
                    cpf = onlyDigits($event.target.value).slice(0, 11);
                    $event.target.value = cpf;
                  "
                />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-user size-4" /> Nome</span>
                <input v-model="nome" placeholder="—" class="ficha-input" />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-calendar size-4" /> Nascimento</span>
                <input v-model="nascimento" type="date" class="ficha-input tabular-nums" />
              </label>
              <label class="ficha-campo">
                <span class="ficha-label"><span class="i-lucide-smartphone size-4" /> Celular</span>
                <input v-model="celular" placeholder="—" class="ficha-input tabular-nums" />
              </label>
            </div>
            <div class="flex flex-wrap gap-4 justify-center items-center mt-4">
              <button
                type="button"
                class="acao-btn text-black-900 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50"
                :disabled="!cpfOk || busy === 'enriquecer'"
                @click="enriquecer"
              >
                <span class="i-lucide-user-plus size-4" />
                {{ busy === 'enriquecer' ? 'Enriquecendo…' : 'Enriquecer' }}
              </button>
              <button
                type="button"
                class="acao-btn text-white bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
                :disabled="busy === 'autorizar'"
                @click="autorizar"
              >
                <span class="i-lucide-landmark size-4" />
                {{ busy === 'autorizar' ? 'Enviando…' : 'Autorizar bancos' }}
              </button>
              <button
                type="button"
                class="acao-btn acao-buscar text-white disabled:opacity-50"
                :disabled="buscando"
                @click="buscarOfertas"
              >
                <span class="i-lucide-search size-4" />
                {{ buscando ? 'Buscando…' : 'Buscar ofertas' }}
              </button>
              <button
                type="button"
                class="acao-btn text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
                :disabled="!cpfOk"
                @click="abrirPropostas"
              >
                <span class="i-lucide-file-text size-4" />
                Consultar propostas
              </button>
            </div>
          </div>

          <!-- estados de autorização (v93: branco negrito, fundo forte) -->
          <div v-if="auth" class="flex flex-wrap gap-2 items-center mb-3">
            <template v-for="b in ['uy3', 'facta', 'c6']" :key="b">
              <span
                v-if="authState(b)"
                class="inline-flex gap-1.5 items-center px-3 py-1.5 text-[11px] font-bold text-white rounded-lg"
                :class="{
                  'bg-n-teal-9': authState(b).cls === 'ok' || authState(b).cls === 'roxo',
                  'bg-n-amber-9': authState(b).cls === 'aguarda',
                  'bg-n-ruby-9': authState(b).cls === 'erro',
                }"
              >
                {{ b.toUpperCase() }} · {{ authState(b).label }}
              </span>
              <button
                v-if="authState(b) && authState(b).link"
                type="button"
                class="px-2.5 py-1.5 text-[11px] font-bold rounded-lg border border-n-teal-9 text-n-teal-11 hover:bg-n-teal-3"
                @click="copiar(authState(b).link, 'Link C6')"
              >
                <span class="i-lucide-clipboard size-3 inline-block align-[-1px]" />
                Copiar link
              </button>
            </template>
          </div>

          <!-- v107: VÍNCULOS EMPREGATÍCIOS — escolher ANTES das ofertas.
               AZUL = ligado/elegível (clicável) · VERMELHO = desligado (trava).
               Com 2+ vínculos, as ofertas só aparecem após a seleção; toda
               oferta/digitação fica amarrada à matrícula selecionada. -->
          <div v-if="vinculos && vinculos.length" class="mb-3">
            <div class="flex gap-2 items-center mb-2">
              <span class="i-lucide-briefcase size-4 text-n-slate-11" />
              <b class="text-sm text-n-slate-12">Vínculos empregatícios</b>
              <span class="text-[11px] text-n-slate-11">
                — selecione o vínculo do empréstimo
              </span>
            </div>
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <button
                v-for="v in vinculos"
                :key="v.matricula"
                type="button"
                class="vinc-card"
                :class="[
                  v.ligado ? 'ligado' : 'desligado',
                  { selecionado: vinculoSel === v.matricula },
                ]"
                :disabled="!v.ligado"
                @click="selecionarVinculo(v)"
              >
                <div class="vinc-topo">
                  <span class="vinc-status">{{ v.ligado ? 'LIGADO' : 'DESLIGADO' }}</span>
                  <span v-if="vinculoSel === v.matricula" class="vinc-check">
                    <span class="i-lucide-check size-3.5" /> SELECIONADO
                  </span>
                </div>
                <p class="vinc-empregador">{{ v.empregador || '—' }}</p>
                <p v-if="v.cnpj" class="vinc-linha">
                  CNPJ: <b class="tabular-nums">{{ v.cnpj }}</b>
                </p>
                <div class="vinc-info">
                  <div>
                    <span>Matrícula</span>
                    <b class="tabular-nums">{{ v.matricula }}</b>
                  </div>
                  <div>
                    <span>Admissão</span>
                    <b>{{ v.admissao ? ddmmyyyy(v.admissao) || v.admissao : '—' }}</b>
                  </div>
                  <div>
                    <span>Salário</span>
                    <b class="tabular-nums">{{ v.salario ? brl(v.salario) : '—' }}</b>
                  </div>
                  <div>
                    <span>Margem</span>
                    <b class="tabular-nums">{{ v.margem ? brl(v.margem) : '—' }}</b>
                  </div>
                </div>
                <p v-if="!v.ligado && v.motivo" class="vinc-motivo">{{ v.motivo }}</p>
                <p class="vinc-fontes">{{ v.fontes.join(' · ') }}</p>
              </button>
            </div>
            <p
              v-if="!mostrarOfertas"
              class="p-2.5 mt-3 text-xs font-semibold text-center rounded-lg border border-dashed border-n-weak text-n-slate-11"
            >
              👆 Selecione um vínculo LIGADO acima para ver as ofertas associadas a ele.
            </p>
          </div>

          <!-- colunas de ofertas (v107: só após vínculo resolvido) -->
          <div v-if="mostrarOfertas" class="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <!-- UY3 (v93: borda azul do banco) -->
            <div class="overflow-hidden rounded-xl border-2 border-n-blue-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-blue-9/40 bg-n-blue-9">
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">UY3</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ uy3Offers ? `${uy3Offers.length} oferta(s)` : '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="uy3Offers === null" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <p v-else-if="!uy3Offers.length" class="py-4 text-xs text-center text-n-slate-11">
                  Sem ofertas UY3
                </p>
                <!-- v100: card no padrão novo do Kairo (credito-card) -->
                <!-- v107: v-show amarra o card ao vínculo selecionado (matrícula
                     do warranty); v-show (e não v-if) preserva o índice do
                     uy3Resim -->
                <div
                  v-for="(card, idx) in uy3Offers || []"
                  v-show="uy3CardVisivel(card)"
                  :key="card.o.id || idx"
                  class="credito-card"
                  :class="{ melhor: isMelhor((card.o.liquidValue || 0) / 100) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ cents(card.o.liquidValue) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor((card.o.liquidValue || 0) / 100)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span v-if="/LEIL[AÃ]O/i.test(card.o.productName || '')" class="cc-badge leilao">
                        Leilão
                      </span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ card.o.numberOfPayments }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ cents(card.parcelaReal != null ? card.parcelaReal : card.o.requestedAmount) }}</strong>
                    </div>
                    <div>
                      <span>Produto</span>
                      <strong>{{ uy3Produto(card.o.productName) }}</strong>
                    </div>
                  </div>
                  <div class="cc-id">
                    ID da simulação: <b class="tabular-nums">{{ card.o.id || '—' }}</b>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: cents(card.o.liquidValue),
                          prazo: card.o.numberOfPayments,
                          parcela: cents(card.parcelaReal != null ? card.parcelaReal : card.o.requestedAmount),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="digBusy"
                      @click="abrirDigitarUY3(idx)"
                    >
                      Digitar esta
                    </button>
                  </div>
                  <p v-if="uy3Resim[idx] && uy3Resim[idx].erro" class="cc-erro">
                    {{ uy3Resim[idx].erro }}
                  </p>
                  <div v-if="uy3Resim[idx]" class="cc-simbox">
                    <label>Simular novo valor</label>
                    <div class="cc-toggle">
                      <button
                        type="button"
                        :class="{ on: uy3Resim[idx].modo === 'Liquid' }"
                        @click="uy3Resim[idx].modo = 'Liquid'"
                      >
                        Valor líquido
                      </button>
                      <button
                        type="button"
                        :class="{ on: uy3Resim[idx].modo === 'Payment' }"
                        @click="uy3Resim[idx].modo = 'Payment'"
                      >
                        Valor da parcela
                      </button>
                    </div>
                    <div class="cc-input-area">
                      <input
                        v-model="uy3Resim[idx].valor"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                      />
                      <button
                        type="button"
                        :disabled="uy3Resim[idx].busy"
                        @click="resimUY3(idx)"
                      >
                        {{ uy3Resim[idx].busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- FACTA (v93: borda âmbar do banco) -->
            <div class="overflow-hidden rounded-xl border-2 border-n-amber-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-amber-9/40 bg-n-amber-9">
                <span class="rounded-sm size-2.5 bg-black-900/70" />
                <b class="text-xs tracking-wider text-black-900">FACTA</b>
                <span class="ml-auto text-[10px] uppercase text-black-900/70">
                  {{ factaState?.ofertas?.length ? `${factaState.ofertas.length} oferta(s)` : factaState?.status || '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="!factaState" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <div v-else-if="factaState.status === 'em_andamento'" class="py-3 text-center">
                  <p class="text-xs text-n-slate-11">Consulta em andamento na FACTA…</p>
                  <button
                    type="button"
                    class="px-3 py-1 mt-2 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                    @click="buscarFACTA"
                  >
                    ↻ Atualizar
                  </button>
                </div>
                <p v-else-if="!factaState.ofertas.length" class="py-4 text-xs text-center text-n-slate-11">
                  {{ factaState.motivo || 'Sem ofertas FACTA' }}
                </p>
                <!-- v100: card no padrão novo do Kairo (credito-card) -->
                <!-- v107: v-show amarra o card ao vínculo selecionado
                     (of.matricula); v-show preserva o índice do factaResim -->
                <div
                  v-for="(of, idx) in factaState?.ofertas || []"
                  v-show="factaCardVisivel(of)"
                  :key="idx"
                  class="credito-card"
                  :class="{ melhor: isMelhor(Number(of.valor_liquido) || 0) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ brl(of.valor_liquido) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor(Number(of.valor_liquido) || 0)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span v-if="of.tabela" class="cc-badge leilao">
                        {{ String(of.tabela || '').split(' ')[0] }}
                      </span>
                      <span v-if="of.matricula" class="cc-badge leilao">
                        Matr. {{ of.matricula }}
                      </span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ of.prazo }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ brl(of.parcela) }}</strong>
                    </div>
                    <div>
                      <span>Produto</span>
                      <strong>{{ of.tabela || '—' }}<template v-if="of.taxa"> · {{ of.taxa }}% a.m.</template></strong>
                    </div>
                  </div>
                  <div class="cc-id">
                    ID da simulação: <b class="tabular-nums">{{ of._idSimulador || '—' }}</b>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: brl(of.valor_liquido),
                          prazo: of.prazo,
                          parcela: brl(of.parcela),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="digBusy || (factaResim[idx] && factaResim[idx].busy)"
                      @click="abrirDigitarFACTA(idx)"
                    >
                      Digitar esta
                    </button>
                  </div>
                  <p v-if="factaResim[idx] && factaResim[idx].erro" class="cc-erro">
                    {{ factaResim[idx].erro }}
                  </p>
                  <div v-if="factaResim[idx]" class="cc-simbox">
                    <label>Simular por parcela</label>
                    <div class="cc-input-area">
                      <input
                        v-model="factaResim[idx].parcela"
                        placeholder="R$ 0,00"
                        class="tabular-nums"
                      />
                      <button
                        type="button"
                        :disabled="factaResim[idx].busy"
                        @click="resimFACTA(idx)"
                      >
                        {{ factaResim[idx].busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- C6 (v93: VERDE com borda verde, pedido do Kairo) -->
            <div class="overflow-hidden rounded-xl border-2 border-n-teal-9/70">
              <div class="flex gap-2 items-center px-3 py-2 border-b border-n-teal-9/40 bg-n-teal-9">
                <span class="bg-white rounded-sm size-2.5" />
                <b class="text-xs tracking-wider text-white">C6</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ c6Sim?.id_simulacao ? 'OFERTA MÁXIMA' : '—' }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="c6Sim === null" class="py-4 text-xs text-center text-n-slate-11">
                  Busque as ofertas
                </p>
                <p v-else-if="c6Sim.aguardando" class="p-3 text-[11px] rounded-lg border border-dashed border-n-teal-7 text-n-teal-11">
                  Libera após a biometria — o cliente precisa concluir o link de
                  autorização C6. Este card mostra o estado autorizado.
                </p>
                <p v-else-if="c6Sim.erro" class="py-4 text-xs text-center text-n-slate-11">
                  {{ c6Sim.erro }}
                </p>
                <!-- v100: card no padrão novo do Kairo (credito-card) -->
                <div
                  v-else
                  class="credito-card"
                  :class="{ melhor: isMelhor(Number(c6Sim.valor_solicitado) || 0) }"
                >
                  <div class="cc-header">
                    <div>
                      <span class="cc-label">Valor liberado</span>
                      <h2 class="cc-valor tabular-nums">{{ brl(c6Sim.valor_solicitado) }}</h2>
                    </div>
                    <div class="cc-badges">
                      <span v-if="isMelhor(Number(c6Sim.valor_solicitado) || 0)" class="cc-badge sucesso">
                        ★ Melhor do cliente
                      </span>
                      <span class="cc-badge maxima">Máxima</span>
                    </div>
                  </div>
                  <div class="cc-info">
                    <div>
                      <span>Prazo</span>
                      <strong>{{ c6Sim.prazo }}x</strong>
                    </div>
                    <div>
                      <span>Parcela</span>
                      <strong class="tabular-nums">{{ brl(c6Sim.valor_parcela) }}</strong>
                    </div>
                    <div>
                      <span>IOF</span>
                      <strong class="tabular-nums">{{ brl(c6Sim.valor_iof) }}</strong>
                    </div>
                  </div>
                  <div class="cc-id">
                    ID da simulação: <b class="tabular-nums">{{ c6Sim.id_simulacao || '—' }}</b>
                  </div>
                  <div class="cc-actions">
                    <button
                      type="button"
                      class="cc-btn"
                      @click="
                        copiarOferta({
                          valor: brl(c6Sim.valor_solicitado),
                          prazo: c6Sim.prazo,
                          parcela: brl(c6Sim.valor_parcela),
                        })
                      "
                    >
                      Copiar
                    </button>
                    <button
                      type="button"
                      class="cc-btn digitar"
                      :disabled="digBusy"
                      @click="abrirDigitarC6"
                    >
                      Digitar esta
                    </button>
                  </div>
                  <div class="cc-simbox">
                    <label>Simular novo valor</label>
                    <div class="cc-toggle">
                      <button
                        type="button"
                        :class="{ on: c6Resim.tipo === 'POR_VALOR_PARCELA' }"
                        @click="c6Resim.tipo = 'POR_VALOR_PARCELA'"
                      >
                        Parcela
                      </button>
                      <button
                        type="button"
                        :class="{ on: c6Resim.tipo === 'POR_VALOR_SOLICITADO' }"
                        @click="c6Resim.tipo = 'POR_VALOR_SOLICITADO'"
                      >
                        Valor
                      </button>
                    </div>
                    <div class="cc-input-area">
                      <input v-model="c6Resim.valor" placeholder="R$ 0,00" class="tabular-nums" />
                      <input v-model="c6Resim.prazo" placeholder="24" class="prazo tabular-nums" />
                      <button
                        type="button"
                        :disabled="c6Resim.busy"
                        @click="resimC6"
                      >
                        {{ c6Resim.busy ? '…' : 'Simular' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: PROPOSTAS (v94) ================= -->
        <template v-else-if="view === 'propostas'">
          <div class="flex flex-wrap gap-2 items-center mb-3">
            <span class="inline-flex gap-1.5 items-center px-2.5 py-1.5 text-[11px] font-bold text-white rounded-lg bg-n-teal-9">
              <span class="inline-block bg-white rounded-full animate-pulse size-2" />
              AO VIVO
            </span>
            <span class="text-xs text-n-slate-11">Propostas do CPF <b class="tabular-nums text-n-slate-12">{{ cpf }}</b></span>
            <span class="flex-1" />
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-bold text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 disabled:opacity-50"
              :disabled="propostasBusy"
              @click="carregarPropostas"
            >
              {{ propostasBusy ? 'Atualizando…' : '↻ Atualizar' }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-xs font-bold text-white bg-red-500 rounded-lg hover:bg-red-600"
              @click="fecharPropostas"
            >
              ✕ Fechar
            </button>
          </div>

          <p v-if="propostas === null" class="py-8 text-sm text-center text-n-slate-11">
            Consultando os 3 bancos em paralelo…
          </p>

          <div v-else class="flex flex-col gap-3">
            <!-- UY3 -->
            <div class="overflow-hidden rounded-xl border-2 border-n-blue-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-blue-9">
                <b class="text-xs tracking-wider text-white">UY3</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('uy3') ? 'ERRO' : `${uy3Items.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('uy3')" class="text-xs text-n-ruby-11">{{ bancoErro('uy3') }}</p>
                <p v-else-if="!uy3Items.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta UY3 para este CPF.
                </p>
                <div v-for="p in uy3Items" :key="p.id" class="p-3 rounded-lg border-2 border-n-blue-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm text-n-slate-12">{{ p.__person_name || `Proposta ${p.creditNoteNo || ''}` }}</b>
                    <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="STATUS_BADGE[statusCls(p.status)]">
                      {{ (p.__resumo?.statusDisplay || p.statusDisplay || p.status || '').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(p.createdAt || p.acceptanceDate) }}</span>
                  </div>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Nº <b class="tabular-nums">{{ p.creditNoteNo || p.id }}</b>
                    <template v-if="p.__resumo?.valorLiquido != null"> · Liberado <b>{{ cents(p.__resumo.valorLiquido) }}</b></template>
                    <template v-if="p.__resumo?.prazo"> · <b>{{ p.__resumo.prazo }}x</b> de <b>{{ cents(p.__resumo.parcela) }}</b></template>
                    <template v-if="p.__resumo"> · Assinatura: <b>{{ Number(p.__resumo.unicoSignScore) > 0 ? 'ASSINADO' : 'não assinou' }}</b></template>
                  </p>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Data da Digitação <b>{{ fmtData(p.__resumo?.dataDigitacao || p.createdAt) || '—' }}</b>
                    · Data de Pagamento <b>{{ fmtData(p.__resumo?.dataPagamento) || '—' }}</b>
                    · CPF <b class="tabular-nums">{{ p.personRegistrationNumber || '—' }}</b>
                  </p>
                  <p class="mt-0.5 text-[11px] text-n-slate-11">
                    UUID <span class="tabular-nums text-n-slate-12">{{ p.id }}</span>
                    · Tabela Digitada <span class="text-n-slate-12">{{ p.__resumo?.produto || p.productIdDisplay || '—' }}</span>
                  </p>
                  <p v-if="p.__resumo?.faseAtual" class="mt-0.5 text-[11px] text-n-slate-11">
                    Fase: {{ p.__resumo.faseAtual.nome }}<template v-if="p.__resumo.faseAtual.descricao"> — {{ p.__resumo.faseAtual.descricao }}</template>
                  </p>
                  <div class="flex flex-wrap gap-1.5 items-center mt-2">
                    <button
                      v-if="extraiUrl(p.__signUrl)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
                      @click="copiar(extraiUrl(p.__signUrl), 'Link de assinatura')"
                    >
                      Copiar link
                    </button>
                    <button
                      v-else-if="!uy3Finalizada(p)"
                      type="button"
                      class="px-3 py-1 text-xs font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                      @click="buscarLinkUY3(p)"
                    >
                      ↻ Buscar link
                    </button>
                    <button
                      v-if="isRevisaoPix(p.__resumo?.statusDisplay || p.statusDisplay || p.status)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-black-900 bg-yellow-400 rounded-lg hover:bg-yellow-500"
                      @click="abrirReapr(p)"
                    >
                      ⚠️ Reapresentar PIX
                    </button>
                    <span class="flex-1" />
                    <button
                      v-if="!uy3Finalizada(p)"
                      type="button"
                      class="px-3 py-1 text-xs font-bold rounded-lg border text-n-ruby-11 border-n-ruby-9 hover:bg-n-ruby-3"
                      @click="cancelarUY3(p)"
                    >
                      Cancelar
                    </button>
                  </div>
                  <!-- reapresentar PIX inline -->
                  <div
                    v-if="reapr[p.id] && reapr[p.id].open"
                    class="flex flex-wrap gap-1.5 items-center p-2 mt-2 rounded-lg border border-dashed border-n-amber-9"
                  >
                    <span class="text-[11px] text-n-slate-11">
                      Chave atual: <b class="text-n-slate-12">{{ p.__resumo?.pixTipoAtual || '—' }} {{ p.__resumo?.pixChaveAtual || '' }}</b>
                    </span>
                    <select
                      v-model="reapr[p.id].tipo"
                      class="px-1.5 py-1 text-[11px] rounded border border-n-weak bg-n-background text-n-slate-12"
                      @change="ajustaChaveReapr(p.id)"
                    >
                      <option value="NaturalRegistrationNumber">CPF</option>
                      <option value="Phone">Telefone</option>
                      <option value="Email">E-mail</option>
                      <option value="Automatic">Aleatória</option>
                    </select>
                    <input
                      v-model="reapr[p.id].chave"
                      placeholder="Chave PIX"
                      class="px-1.5 py-1 w-44 text-[11px] rounded border border-n-weak bg-n-background text-n-slate-12"
                      :disabled="reapr[p.id].tipo === 'NaturalRegistrationNumber'"
                    />
                    <button
                      type="button"
                      class="px-2.5 py-1 text-[11px] font-bold text-black-900 bg-yellow-400 rounded-lg hover:bg-yellow-500 disabled:opacity-50"
                      :disabled="reapr[p.id].busy"
                      @click="submitReapr(p.id)"
                    >
                      {{ reapr[p.id].busy ? '…' : 'Reapresentar' }}
                    </button>
                    <button
                      type="button"
                      class="px-2 py-1 text-[11px] rounded-lg border border-n-weak text-n-slate-11"
                      @click="reapr[p.id].open = false"
                    >
                      fechar
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- FACTA -->
            <div class="overflow-hidden rounded-xl border-2 border-n-amber-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-amber-9">
                <b class="text-xs tracking-wider text-black-900">FACTA</b>
                <span class="ml-auto text-[10px] uppercase text-black-900/70">
                  {{ bancoErro('facta') ? 'ERRO' : `${factaItems.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('facta')" class="text-xs text-n-ruby-11">{{ bancoErro('facta') }}</p>
                <p v-else-if="!factaItems.length" class="py-3 text-xs text-center text-n-slate-11">
                  {{ propostas.facta?.mensagem || 'Nenhuma proposta FACTA para este CPF.' }}
                </p>
                <div v-for="(p, i) in factaItems" :key="i" class="p-3 rounded-lg border-2 border-n-amber-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm tabular-nums text-n-slate-12">Nº {{ p.codigo || p.proposta || '—' }}</b>
                    <span class="px-2 py-0.5 text-[10px] font-bold rounded" :class="STATUS_BADGE[statusCls(p.situacao || p.status)]">
                      {{ (p.situacao || p.status || '').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ p.data_emissao || p.data_ccb || '' }}</span>
                  </div>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Valor <b>{{ brl(p.valor_ccb || p.valor) }}</b>
                  </p>
                  <div v-if="p.url_assinatura" class="flex gap-1.5 items-center mt-2">
                    <button
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
                      @click="copiar(p.url_assinatura, 'Link FACTA')"
                    >
                      Copiar link
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- C6 -->
            <div class="overflow-hidden rounded-xl border-2 border-n-teal-9/70">
              <div class="flex gap-2 items-center px-3 py-2 bg-n-teal-9">
                <b class="text-xs tracking-wider text-white">C6</b>
                <span class="ml-auto text-[10px] uppercase text-white/80">
                  {{ bancoErro('c6') ? 'ERRO' : `${c6Items.length} proposta(s)` }}
                </span>
              </div>
              <div class="flex flex-col gap-2 p-2.5">
                <p v-if="bancoErro('c6')" class="text-xs text-n-ruby-11">{{ bancoErro('c6') }}</p>
                <p v-else-if="!c6Items.length" class="py-3 text-xs text-center text-n-slate-11">
                  Nenhuma proposta C6 para este CPF.
                </p>
                <div v-for="r in c6Items" :key="r.proposta_id" class="p-3 rounded-lg border-2 border-n-teal-7 bg-n-solid-1">
                  <div class="flex flex-wrap gap-2 items-center">
                    <b class="text-sm tabular-nums text-n-slate-12">Nº {{ r.proposta_id }}</b>
                    <span
                      class="px-2 py-0.5 text-[10px] font-bold rounded"
                      :class="STATUS_BADGE[statusCls(r.detalhe?.status || r.detalhe?.situacao)]"
                    >
                      {{ (r.detalhe?.status || r.detalhe?.situacao || '—').toUpperCase() }}
                    </span>
                    <span class="flex-1" />
                    <span class="text-[11px] text-n-slate-11">{{ fmtData(r.criado_em) }}</span>
                  </div>
                  <p class="mt-1 text-[12px] text-n-slate-12">
                    Valor <b>{{ brl(r.detalhe?.valor || r.detalhe?.valor_total) }}</b>
                    <span v-if="r.error" class="text-n-ruby-11"> · {{ r.error }}</span>
                  </p>
                  <div v-if="extraiUrl(r.link)" class="flex gap-1.5 items-center mt-2">
                    <button
                      type="button"
                      class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
                      @click="copiar(extraiUrl(r.link), 'Link C6 (formalização)')"
                    >
                      Copiar link
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: DIGITAR ================= -->
        <template v-else-if="view === 'digitar' && dig">
          <div class="grid grid-cols-1 gap-3 lg:grid-cols-[320px_1fr]">
            <!-- resumo (v94: borda na cor do banco) -->
            <div
              class="p-4 h-fit rounded-xl border-2 bg-n-solid-1"
              :class="{
                'border-n-blue-9/70': dig.banco === 'UY3',
                'border-n-amber-9/70': dig.banco === 'FACTA',
                'border-n-teal-9/70': dig.banco === 'C6',
              }"
            >
              <span
                class="px-2 py-0.5 text-xs font-bold rounded"
                :class="{
                  'bg-n-blue-3 text-n-blue-11': dig.banco === 'UY3',
                  'bg-n-amber-3 text-n-amber-11': dig.banco === 'FACTA',
                  'bg-n-teal-3 text-n-teal-11': dig.banco === 'C6',
                }"
              >
                {{ dig.banco }}
              </span>
              <p class="mt-3 text-[10px] uppercase tracking-wide text-n-slate-11">
                Valor liberado
              </p>
              <p class="text-2xl font-bold tabular-nums text-n-teal-11">
                {{ dig.resumo.valor }}
              </p>
              <div class="flex flex-col gap-1 mt-3 text-xs">
                <span class="flex justify-between text-n-slate-11">
                  Prazo <b class="text-n-slate-12">{{ dig.resumo.prazo }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  Parcela <b class="text-n-slate-12">{{ dig.resumo.parcela }}</b>
                </span>
                <span v-if="dig.resumo.extra" class="flex justify-between text-n-slate-11">
                  Produto <b class="text-right text-n-slate-12">{{ dig.resumo.extra }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  Cliente <b class="text-n-slate-12">{{ nome }}</b>
                </span>
                <span class="flex justify-between text-n-slate-11">
                  CPF <b class="tabular-nums text-n-slate-12">{{ cpf }}</b>
                </span>
              </div>
            </div>

            <!-- formulário (v94: borda verde no card inteiro) -->
            <div class="p-4 rounded-xl border-2 border-green-400/70 bg-n-solid-1">
              <h4 class="mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                Cliente
              </h4>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label class="text-xs text-n-slate-11">
                  Nome completo
                  <input v-model="form.nome" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Nascimento
                  <input v-model="form.nascimento" type="date" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  E-mail
                  <input v-model="form.email" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Telefone
                  <input v-model="form.telefone" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                </label>
                <label v-if="dig.banco === 'UY3'" class="text-xs text-n-slate-11">
                  Estado civil
                  <select v-model="form.civilStatus" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                    <option value="Single">Solteiro(a)</option>
                    <option value="Married">Casado(a)</option>
                    <option value="Divorced">Divorciado(a)</option>
                    <option value="Widowed">Viúvo(a)</option>
                  </select>
                </label>
                <label v-if="dig.banco === 'UY3'" class="text-xs text-n-slate-11">
                  Profissão
                  <input v-model="form.occupation" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <template v-if="dig.banco === 'FACTA'">
                  <label class="text-xs text-n-slate-11">
                    Sexo
                    <select v-model="form.sexo" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Estado civil
                    <select v-model="form.estadoCivil" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="solteiro">Solteiro(a)</option>
                      <option value="casado">Casado(a)</option>
                      <option value="divorciado">Divorciado(a)</option>
                      <option value="viuvo">Viúvo(a)</option>
                      <option value="separado">Separado(a)</option>
                      <option value="uniao">União estável</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Nome da mãe
                    <input v-model="form.nomeMae" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    RG
                    <input v-model="form.rg" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Órgão emissor
                    <input v-model="form.rgOrgao" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    UF do RG
                    <input v-model="form.rgUf" maxlength="2" class="px-3 py-2 mt-1 w-full text-sm uppercase rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Expedição (dd/mm/aaaa)
                    <input v-model="form.rgData" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Renda (R$)
                    <input v-model="form.renda" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Matrícula
                    <input v-model="form.matricula" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                  </label>
                </template>
              </div>

              <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                Endereço
                <span class="ml-1 font-normal normal-case text-n-slate-11">(preenchido pelo CEP)</span>
              </h4>
              <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label class="text-xs text-n-slate-11">
                  CEP
                  <div class="flex gap-1.5 mt-1">
                    <input v-model="form.cep" class="px-3 py-2 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" @blur="buscarCep" />
                    <button
                      type="button"
                      class="px-3 text-xs font-bold text-white rounded-lg bg-n-blue-9 hover:bg-n-blue-10 shrink-0"
                      @click="buscarCep"
                    >
                      Buscar CEP
                    </button>
                  </div>
                </label>
                <label class="text-xs text-n-slate-11">
                  Rua
                  <input v-model="form.rua" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Número
                  <input v-model="form.numero" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Complemento
                  <input v-model="form.complemento" placeholder="Apto / bloco (opcional)" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Bairro
                  <input v-model="form.bairro" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  Cidade
                  <input v-model="form.cidade" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
                <label class="text-xs text-n-slate-11">
                  UF
                  <input v-model="form.uf" maxlength="2" class="px-3 py-2 mt-1 w-full text-sm uppercase rounded-lg border border-n-weak bg-n-background text-n-slate-12" />
                </label>
              </div>

              <!-- PIX (UY3/FACTA) ou dados bancários (C6) -->
              <template v-if="dig.banco !== 'C6'">
                <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                  Conta PIX
                </h4>
                <div class="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <label class="text-xs text-n-slate-11">
                    Tipo de chave
                    <select v-model="form.pixTipo" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <template v-if="dig.banco === 'UY3'">
                        <option value="NaturalRegistrationNumber">CPF</option>
                        <option value="Phone">Telefone</option>
                        <option value="Email">E-mail</option>
                        <option value="Automatic">Aleatória</option>
                      </template>
                      <template v-else>
                        <option value="1">CPF</option>
                        <option value="2">Telefone</option>
                        <option value="3">E-mail</option>
                        <option value="4">Aleatória</option>
                      </template>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Chave PIX
                    <input v-model="form.pixChave" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                </div>
              </template>
              <template v-else>
                <h4 class="mt-4 mb-2 text-sm font-bold tracking-wider uppercase text-n-teal-11">
                  Dados bancários
                </h4>
                <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <label class="text-xs text-n-slate-11">
                    Tipo de conta
                    <select v-model="form.tipoConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12">
                      <option value="ContaCorrenteIndividual">Conta Corrente Individual</option>
                    </select>
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Banco (nº)
                    <input v-model="form.numeroBanco" placeholder="033" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Agência
                    <input v-model="form.numeroAgencia" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Dígito agência
                    <input v-model="form.digitoAgencia" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Conta
                    <input v-model="form.numeroConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                  <label class="text-xs text-n-slate-11">
                    Dígito conta
                    <input v-model="form.digitoConta" class="px-3 py-2 mt-1 w-full text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 tabular-nums" />
                  </label>
                </div>
              </template>

              <div class="flex gap-2 items-center pt-3 mt-4 border-t border-n-weak">
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90 disabled:opacity-50"
                  :disabled="digBusy"
                  @click="confirmarDigitacao"
                >
                  {{ digBusy ? 'Digitando…' : 'Confirmar digitação' }}
                </button>
                <button
                  type="button"
                  class="px-4 py-2 text-sm font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                  @click="voltarOfertas"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </template>

        <!-- ================= VIEW: RESULTADO ================= -->
        <template v-else-if="view === 'resultado' && resultado">
          <div class="p-6 mx-auto max-w-lg text-center rounded-xl border border-n-teal-7 bg-n-teal-2">
            <span class="inline-block i-lucide-check-circle size-10 text-n-teal-11" />
            <h3 class="mt-2 text-lg font-bold text-n-slate-12">
              Proposta {{ resultado.banco }} digitada!
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">{{ resultado.msg }}</p>
            <p v-if="resultado.numero" class="mt-2 text-xs tabular-nums text-n-slate-11">
              Nº: <b class="text-n-slate-12">{{ resultado.numero }}</b>
            </p>
            <div v-if="resultado.link" class="flex gap-2 justify-center items-center p-2 mt-3 rounded-lg border border-n-weak bg-n-background">
              <span class="text-xs truncate text-n-slate-12">{{ resultado.link }}</span>
              <button
                type="button"
                class="px-3 py-1 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90 shrink-0"
                @click="copiar(resultado.link, 'Link')"
              >
                Copiar
              </button>
            </div>
            <button
              type="button"
              class="px-4 py-2 mt-4 text-sm font-semibold rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
              @click="voltarOfertas"
            >
              ← Voltar às ofertas
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ============================================================================
   v100 — cards de oferta no padrão novo do Kairo (HTML/CSS fornecido por ele,
   convertido pra classes cc-*) + ficha/header com ícones verdes.
   Escopado: NÃO vaza pro resto do Chatwoot.
   ========================================================================= */

/* ---- card de oferta ---- */
.credito-card {
  width: 100%;
  background: linear-gradient(180deg, #17181d, #111216);
  border: 1px solid rgba(30, 211, 190, 0.75);
  border-radius: 16px;
  padding: 16px;
  color: #ffffff;
  box-shadow:
    0 0 0 1px rgba(30, 211, 190, 0.15),
    0 12px 30px rgba(0, 0, 0, 0.35);
}
.credito-card.melhor {
  border-color: #1ee7ca;
  box-shadow:
    0 0 0 2px rgba(30, 231, 202, 0.35),
    0 12px 30px rgba(0, 0, 0, 0.35);
}
.cc-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}
.cc-label {
  font-size: 12px;
  color: #9ca3af;
  display: block;
  margin-bottom: 4px;
}
.cc-valor {
  margin: 0;
  color: #18e0c3;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.3px;
}
.cc-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}
.cc-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 9px;
  border-radius: 999px;
  white-space: nowrap;
}
.cc-badge.sucesso,
.cc-badge.maxima {
  color: #1ee7ca;
  background: rgba(30, 231, 202, 0.12);
}
.cc-badge.leilao {
  color: #ffc247;
  background: rgba(255, 194, 71, 0.14);
}
/* blocos Prazo/Parcela/Produto — rótulo e valor CENTRALIZADOS (pedido) */
.cc-info {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
}
.cc-info > div {
  background: #202127;
  border: 1px solid #2b2d35;
  border-radius: 12px;
  padding: 10px;
  text-align: center;
}
.cc-info span {
  display: block;
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 4px;
}
.cc-info strong {
  font-size: 14px;
  color: #ffffff;
  word-break: break-word;
}
.cc-id {
  margin-top: 10px;
  padding: 11px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  color: #d1d5db;
  font-size: 12px;
  word-break: break-all;
}
.cc-id b {
  color: #ffffff;
}
.cc-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
}
.cc-btn {
  border: none;
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
  flex: 1;
  font-size: 13px;
  background: #19b8a7;
}
.cc-btn.digitar {
  background: #14a99a;
}
.cc-btn:hover {
  opacity: 0.9;
}
.cc-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cc-erro {
  margin-top: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #dc2626;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
}
/* ---- caixa de simular ---- */
.cc-simbox {
  margin-top: 14px;
  padding: 13px;
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  background: #18191f;
}
.cc-simbox > label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #d1d5db;
  margin-bottom: 8px;
}
.cc-toggle {
  display: inline-flex;
  background: #101116;
  border: 1px solid #2b2d35;
  border-radius: 9px;
  padding: 3px;
  margin-bottom: 9px;
}
.cc-toggle button {
  border: none;
  background: transparent;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 7px;
  cursor: pointer;
}
.cc-toggle button.on {
  background: #18e0c3;
  color: #062b27;
}
.cc-input-area {
  display: flex;
  gap: 8px;
}
.cc-input-area input {
  flex: 1;
  min-width: 0;
  height: 42px;
  border-radius: 10px;
  border: 1px solid #2dd4bf;
  background: #ffffff;
  padding: 0 12px;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  outline: none;
}
.cc-input-area input.prazo {
  flex: 0 0 56px;
  text-align: center;
}
.cc-input-area > button {
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #ffc247, #ffb020);
  color: #111827;
  font-weight: 800;
  padding: 0 16px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
}
.cc-input-area > button:disabled {
  opacity: 0.5;
  cursor: default;
}
/* ---- header do modal ---- */
.sim-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(30, 231, 202, 0.12);
  color: #1ee7ca;
  border: 1px solid rgba(30, 231, 202, 0.35);
}
/* ---- ficha (CPF/Nome/Nascimento/Celular) ---- */
.ficha-grid {
  display: grid;
  grid-template-columns: 1.1fr 2fr 1fr 1fr;
  gap: 12px;
  padding: 14px;
  border-radius: 12px;
  background: #101116;
  border: 1px solid #23252c;
}
.ficha-campo {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 14px;
  border-left: 1px solid #23252c;
}
.ficha-campo:first-child {
  border-left: none;
  padding-left: 0;
}
@media (max-width: 800px) {
  .ficha-grid {
    grid-template-columns: 1fr 1fr;
  }
  .ficha-campo {
    border-left: none;
    padding-left: 0;
  }
}
.ficha-label {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  color: #9ca3af;
}
.ficha-label > span {
  color: #1ee7ca;
}
.ficha-input {
  background: transparent;
  border: none;
  outline: none;
  color: #ffffff;
  font-weight: 800;
  font-size: 15px;
  padding: 0;
  width: 100%;
}
.ficha-input::placeholder {
  color: #4b5563;
}
.ficha-input-erro {
  color: #f87171 !important;
}
input[type='date'].ficha-input {
  color-scheme: dark;
}
/* ---- v107: cards de VÍNCULO EMPREGATÍCIO ---- */
.vinc-card {
  text-align: left;
  border-radius: 14px;
  padding: 12px;
  border: 2px solid;
  background: linear-gradient(180deg, #17181d, #111216);
  color: #ffffff;
  cursor: pointer;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}
.vinc-card.ligado {
  border-color: #1f6feb;
  box-shadow: 0 0 0 1px rgba(31, 111, 235, 0.25);
}
.vinc-card.ligado:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 0 2px rgba(31, 111, 235, 0.45);
}
.vinc-card.desligado {
  border-color: #e5484d;
  box-shadow: 0 0 0 1px rgba(229, 72, 77, 0.25);
  cursor: not-allowed;
  opacity: 0.85;
}
.vinc-card.selecionado {
  border-color: #1ee7ca;
  box-shadow: 0 0 0 3px rgba(30, 231, 202, 0.35);
}
.vinc-topo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  gap: 8px;
}
.vinc-status {
  font-size: 10px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 999px;
  letter-spacing: 0.04em;
}
.ligado .vinc-status {
  color: #79b8ff;
  background: rgba(31, 111, 235, 0.18);
}
.desligado .vinc-status {
  color: #ff9592;
  background: rgba(229, 72, 77, 0.18);
}
.vinc-check {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  font-size: 10px;
  font-weight: 800;
  color: #062b27;
  background: #1ee7ca;
  padding: 3px 8px;
  border-radius: 999px;
}
.vinc-empregador {
  font-size: 14px;
  font-weight: 800;
  word-break: break-word;
}
.vinc-linha {
  margin-top: 2px;
  font-size: 11px;
  color: #9ca3af;
}
.vinc-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 8px;
}
.vinc-info > div {
  background: #202127;
  border: 1px solid #2b2d35;
  border-radius: 10px;
  padding: 7px 9px;
  text-align: center;
}
.vinc-info span {
  display: block;
  font-size: 10px;
  color: #9ca3af;
}
.vinc-info b {
  font-size: 12px;
  color: #ffffff;
}
.vinc-motivo {
  margin-top: 8px;
  font-size: 11px;
  font-weight: 700;
  color: #ff9592;
}
.vinc-fontes {
  margin-top: 6px;
  font-size: 10px;
  color: #6b7280;
  text-align: right;
}

/* ---- botões de ação da ficha ---- */
.acao-btn {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  height: 40px;
  padding: 0 18px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 13px;
}
.acao-buscar {
  background: #16a34a;
}
.acao-buscar:hover {
  background: #15803d;
}
</style>
